import {
  useCreateEncryptionContext,
  useUpdateRequestSessionKeys,
} from "apollo/hooks/mutations";
import { useGetAuctionRequestEvents } from "apollo/hooks/queries";
import { useOnRequestEventReceived } from "apollo/hooks/subscriptions";
import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { useEnvContext } from "context/EnvContext";
import {
  ACCOUNT_TYPE_STAFF,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  TRACK_EVENTS,
} from "core/consts";
import { useSafeState } from "core/hooks";
import api from "core/model/api";
import { importPrivateKey } from "core/model/crypto";
import {
  decryptMessage,
  decryptSessionKey,
  generateExtraSessionKey,
} from "core/model/crypto/cryptoService";
import { getPatientSessionKey } from "core/model/patients/encryption";
import { useIsLoggedSealdOnly } from "core/model/patients/encryption/useIsLoggedSealdOnly";
import { useActivityContext } from "core/model/utils/browser/ActivitySingleton";
import { computeSealdDisplayId, decryptFromSession } from "core/seald";
import { useSealdContext } from "core/seald/SealdContext";
import {
  Account,
  AccountInAuctionRequest,
  AuctionRequest,
  EncryptionContext as EncryptionContextType,
  Event,
  EventContext,
  PendingSessionKey,
  QueryProgress,
  SessionKey,
  TrackEventFn,
} from "core/types";
import { getUnixTime } from "date-fns";
import SpinnerPage from "ds_legacy/components/Spinner";
import { AuctionRequestEventsContext } from "dsl/atoms/AuctionRequestEventsContext";
import { EncryptionContextProvider } from "dsl/atoms/Contexts";
import ErrorPage from "dsl/ecosystems/ErrorPage";
import { useUpdatedValue } from "dsl/hooks/useUpdatedValue";
import { useStatusTranslation } from "dsl/molecules/Messenger/EventTitleCareseeker";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useTracking } from "react-tracking";
import {
  useConnecterWithLoggedAccount,
  useLoggedCareprovider,
  useLoggedCareseeker,
  usePrivateKey,
} from "reduxentities/hooks";
import {
  fetchKeysdecryptKey,
  generateSessionKeys,
  isEncryptionAvailableForUser,
} from "./shared";

async function decryptEventContext(
  context: EventContext,
  decryptedSessionKey: CryptoKey,
) {
  if (
    !context.file?.encrypted_name?.content ||
    !context.file?.encrypted_name?.iv
  )
    return context;

  try {
    const decrypted = await decryptMessage({
      iv: context.file.encrypted_name.iv,
      message: context.file.encrypted_name.content,
      sessionKey: decryptedSessionKey,
    });

    return {
      ...context,
      file: {
        ...context.file,
        encrypted_name: {
          ...context.file.encrypted_name,
          decrypted,
        },
      },
    };
  } catch (e) {
    console.error("Could not decrypt event context", e);
  }
}

/** @deprecated To be removed when old encrytion gets decommissioned */
export async function decryptEvent(
  event: Event,
  decryptedSessionKey: CryptoKey,
  trackEvent: TrackEventFn,
) {
  if (!event.context) return event;

  try {
    let message = event.context.message_iv
      ? await decryptMessage({
          iv: event.context.message_iv,
          message: event?.context?.message || "",
          sessionKey: decryptedSessionKey,
        })
      : event.context.message;

    if (!message && event.context.seald_message) {
      message = await decryptFromSession({
        context: "event message",
        encryptedMessage: event.context.seald_message,
      });
    }

    return {
      ...event,
      context: await decryptEventContext(
        {
          ...event.context,
          decrypted: true,
          message,
        },
        decryptedSessionKey,
      ),
    };
  } catch (error) {
    console.log(
      `%c Could not deserialize message ${error}`,
      "background: red; color: white",
    );

    trackEvent({
      event_id: event.id?.toString(),
      name: TRACK_EVENTS.MESSAGE_DESERIALIZATION_ERROR,
    });
  }

  return event;
}

export async function decryptSealdEvent(
  event: Event,
  trackEvent: TrackEventFn,
) {
  if (!event.context) return event;

  try {
    const sealdDecryptedMessage = await decryptFromSession({
      context: "event message",
      encryptedMessage: event.context.seald_message,
    });

    return {
      ...event,
      context: {
        ...event.context,
        decrypted: !!sealdDecryptedMessage,
        message: sealdDecryptedMessage ?? event.context.message,
      },
    };
  } catch (error) {
    console.error(
      `%c Could not deserialize message ${error}`,
      "background: red; color: white",
    );

    trackEvent({
      event_id: event.id?.toString(),
      name: TRACK_EVENTS.MESSAGE_DESERIALIZATION_ERROR,
    });
  }

  return event;
}

async function decryptSealdEvents(
  events: Array<Event>,
  trackEvent: TrackEventFn,
  setDecryptedEvents: any,
) {
  if (events) {
    const decrypted = await Promise.all(
      events.map((event) => decryptSealdEvent(event, trackEvent)),
    );

    setDecryptedEvents(decrypted);
  }
}

/** @deprecated To be removed when old encrytion gets decommissioned */
async function fetchKeysdecryptEvent(
  decryptedSessionKey: any,
  events: Array<Event>,
  trackEvent: TrackEventFn,
  setDecryptedEvents: any,
) {
  if (!decryptedSessionKey) return;
  if (events) {
    const decrypted = await Promise.all(
      events.map(async (e) => decryptEvent(e, decryptedSessionKey, trackEvent)),
    );

    setDecryptedEvents(decrypted);
  }
}

function PendingEventDecryption() {
  const { trackEvent } = useTracking();

  // If the decryption fails, the user ends up in this view forever.
  // It should never happen.
  useEffect(() => {
    const timer = setTimeout(() => {
      trackEvent({ name: TRACK_EVENTS.EVENT_DECRYPTION_ERROR });
      console.error("Event decryption error");
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  });

  return <SpinnerPage id="event_decryption" />;
}

/** @deprecated To be removed when old encryption gets decommissioned */
const FetchKeysHOC = ({
  account,
  accounts,
  children,
  context,
  events,
  privateKey,
  resourceId,
  sessionKey,
  updateSessionKey,
  withEncryptionAccess,
}: {
  account: Account;
  accounts: (AccountInAuctionRequest | Account)[] | null;
  children: React.ReactNode;
  context: Record<string, any>;
  events: Array<Event>;
  privateKey: string | undefined;
  resourceId: number;
  sessionKey: SessionKey | null;
  updateSessionKey: (s: any) => void;
  withEncryptionAccess: boolean;
}) => {
  const [accessDenied, setAccessDenied] = useSafeState<boolean>(false);
  const [decryptedSessionKey, setDecryptedSessionKey] =
    useSafeState<CryptoKey | null>(null);
  const { trackEvent } = useTracking();

  const [decryptedEvents, setDecryptedEvents] = useSafeState<Event[] | null>(
    null,
  );

  useLayoutEffect(() => {
    generateSessionKeys(
      trackEvent,
      sessionKey,
      accounts,
      context,
      account,
      updateSessionKey,
    );
  }, [resourceId]);

  useLayoutEffect(() => {
    fetchKeysdecryptKey({
      context,
      privateKey,
      sessionKey,
      setAccessDenied,
      setDecryptedSessionKey,
      trackEvent,
    });
  }, [resourceId, sessionKey]);

  useLayoutEffect(() => {
    fetchKeysdecryptEvent(
      decryptedSessionKey,
      events,
      trackEvent,
      setDecryptedEvents,
    );
  }, [decryptedSessionKey, events.length]);

  useEffect(() => {
    if (decryptedEvents && events && decryptedEvents.length != events.length) {
      decryptedEvents.push(events[events.length - 1]);
    }
  });

  const eventsDecrypted = decryptedEvents != null;

  if (!accessDenied && !eventsDecrypted) return <PendingEventDecryption />;

  return (
    <EncryptionContextProvider
      value={{
        account,
        decryptedSessionKey,
        events: decryptedEvents,
        // not seald only patient, but has default encryption
        // dictated by previous conditionals
        hasEncryption: true,
        isSealdOnly: false,
        userSupportsEncryption: !accessDenied,
        withEncryptionAccess,
      }}
    >
      {children}
    </EncryptionContextProvider>
  );
};

const SealdEventsProvider = ({
  account,
  children,
  events,
  sealdEncryptionContext,
  withEncryptionAccess,
}: {
  account: Account;
  children: React.ReactNode;
  events: Array<Event>;
  sealdEncryptionContext: EncryptionContextType | undefined;
  withEncryptionAccess: boolean;
}) => {
  const { trackEvent } = useTracking();
  const hasSealdEncryptionContext = !!sealdEncryptionContext;
  const [decryptedEvents, setDecryptedEvents] = useSafeState<Event[] | null>(
    null,
  );

  useLayoutEffect(() => {
    if (hasSealdEncryptionContext)
      decryptSealdEvents(events, trackEvent, setDecryptedEvents);
  }, [events.length]);

  useEffect(() => {
    if (decryptedEvents && events && decryptedEvents.length != events.length) {
      decryptedEvents.push(events[events.length - 1]);
    }
  });

  const eventsDecrypted = decryptedEvents != null;

  if (account?.account_type !== ACCOUNT_TYPE_STAFF) {
    if (!hasSealdEncryptionContext) {
      return (
        <ErrorPage
          getErrorLabel={(translations) =>
            translations.general.banner.cta.partialEncryptionAccessInfo
          }
          pageName="Pending encryption access to data"
        />
      );
    }

    if (!eventsDecrypted) {
      return <PendingEventDecryption />;
    }
  }

  return (
    <EncryptionContextProvider
      value={{
        account,
        decryptedSessionKey: null,
        events: decryptedEvents,
        hasEncryption: hasSealdEncryptionContext,
        isSealdOnly: true,
        userSupportsEncryption: true,
        withEncryptionAccess,
      }}
    >
      {children}
    </EncryptionContextProvider>
  );
};

function getPendingKeyType(key: PendingSessionKey) {
  if (key.patient_id) return "patient";
  if (key.auction_request_id) return "request";
  if (key.careprovider_id) return "provider";
  console.error("Could not find pending key type", key);
  return "undefined";
}

async function restorePendingKey({
  key,
  privateKey,
  trackEvent,
}: {
  key: PendingSessionKey;
  privateKey: any;
  trackEvent: TrackEventFn;
}) {
  const errorContext: [string, Record<string, any>] = [
    "Pending Key decryption",
    {
      auction_request_id: key.auction_request_id,
      careprovider_id: key.careprovider_id,
      careseeker_id: key.careseeker_id,
      destination_account_id: key.account_id,
      patient_id: key.patient_id,
      pending_key_type: getPendingKeyType(key),
      status: "error",
    },
  ];
  try {
    const importedPrivateKey = await importPrivateKey(privateKey);
    if (!importedPrivateKey) {
      trackEvent({
        error: JSON.stringify(errorContext),
        name: TRACK_EVENTS.IMPORTED_PRIVATE_KEY,
      });

      return;
    }
    const decrypted = await decryptSessionKey(
      key?.user_session_key,
      importedPrivateKey,
      key?.algorithm,
    );

    if (!decrypted) {
      trackEvent({
        error: JSON.stringify(errorContext),
        name: TRACK_EVENTS.DECRYPT_SESSION_KEY,
      });

      return;
    }

    const generated = await generateExtraSessionKey(decrypted, {
      id: key.account_id,
      public_key: key.account_public_key as unknown as CryptoKey,
    });

    try {
      await api.crypto.updatePendingSessionKey({
        ...generated,
        algorithm: null,
        auction_request_id: key.auction_request_id,
        careprovider_id: key.careprovider_id,
        careseeker_id: key.careseeker_id,
        patient_id: key.patient_id,
        updated_at: getUnixTime(new Date()),
      });
    } catch (e) {
      console.groupCollapsed("Could not update pending session key");
      console.error("Could not update pending session key", e);
      console.groupEnd();
    }

    trackEvent({
      destination_account_id: key.account_id,
      name: TRACK_EVENTS.PENDING_KEY_DECRYPTION,
      pending_key_type: getPendingKeyType(key),
      status: "success",
    });
  } catch (err) {
    trackEvent({
      error: JSON.stringify(errorContext),
      name: TRACK_EVENTS.MESSAGE_DESERIALIZATION_ERROR,
    });

    console.error("error restoring pending session keys - ", err);
  }
}

async function handlePendingKeys(
  keys: Array<PendingSessionKey> | null | undefined,
  privateKey: any,
  trackEvent: TrackEventFn,
  delay?: number,
) {
  if (keys == null) return;
  for (let i = 0; i < keys.length; i += 1) {
    // we are aware waiting in  each iteration
    // prevents some of the async advantages
    // however, this is a CPU heavy operation and
    // we want to make sure only one is run at a
    // time, to not overload the CPU

    await restorePendingKey({ key: keys[i], privateKey, trackEvent });

    await new Promise((t) => setTimeout(t, delay != null ? delay : 500));
  }
}

export function useGetPendingKeys() {
  const { trackEvent } = useTracking();

  const privateKey = usePrivateKey();
  const [manualDone, setManualDone] = useSafeState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const processPendingKeys = useCallback(async () => {
    if (privateKey != null) {
      api.crypto
        .getPendingKeys()
        .then((keys) => handlePendingKeys(keys, privateKey, trackEvent, 0));
    }
  }, [privateKey]);

  const getPendingKeys = useCallback(async () => {
    return new Promise<void>((resolve) => {
      setManualDone(QUERY_PROGRESS_PENDING);
      processPendingKeys()
        .then(() => setManualDone(QUERY_PROGRESS_SUCCEED))
        .catch(() => setManualDone(QUERY_PROGRESS_FAILED))
        .finally(() => resolve());
    });
  }, [processPendingKeys, setManualDone]);

  return { getPendingKeys, manualDone };
}

export function useRestorePendingKeys(privateKey: string | undefined) {
  const { trackEvent } = useTracking();
  const { active } = useActivityContext();

  useEffect(() => {
    if (!privateKey) return;

    async function regenerateSessionKeysFromPending() {
      api.crypto
        .getPendingKeys()
        .then((keys) => handlePendingKeys(keys, privateKey, trackEvent));
    }

    // Run it on mount
    regenerateSessionKeysFromPending();

    // We have no way to "push" at the moment.
    // So we pull every X minutes
    const intervalToPullInMinutes = active ? 2 : 60;
    const id = setInterval(
      () => {
        regenerateSessionKeysFromPending();
      },
      intervalToPullInMinutes * 60 * 1000,
    );

    return () => {
      clearInterval(id);
    };
  }, [privateKey, active]);
}

function useAuctionRequestSealdSession(
  auctionRequest: AuctionRequest | null | undefined,
) {
  const hasEncryptionContext = !!auctionRequest?.has_seald_encryption_context;
  const [isProcessed, setIsProcessed] = useState(hasEncryptionContext);
  const [createEncryptionContext] = useCreateEncryptionContext();
  const createSealdSession = useSealdContext()?.createSealdSession;
  const envContext = useEnvContext();
  const loggedCareseeker = useLoggedCareseeker();
  const loggedCareprovider = useLoggedCareprovider();

  let loggedGroupDisplayId = null;
  if (loggedCareseeker?.id) {
    loggedGroupDisplayId = computeSealdDisplayId({
      envContext,
      id: loggedCareseeker.id,
      type: "careseeker",
    });
  } else if (loggedCareprovider?.id) {
    loggedGroupDisplayId = computeSealdDisplayId({
      envContext,
      id: loggedCareprovider.id,
      type: "careprovider",
    });
  }

  const careseekerDisplayId = auctionRequest?.auction?.patient?.careseeker?.id
    ? computeSealdDisplayId({
        envContext,
        id: auctionRequest.auction.patient.careseeker.id,
        type: "careseeker",
      })
    : undefined;
  const careproviderDisplayId = auctionRequest?.careprovider_id
    ? computeSealdDisplayId({
        envContext,
        id: auctionRequest.careprovider_id,
        type: "careprovider",
      })
    : undefined;

  useEffect(() => {
    if (
      !hasEncryptionContext &&
      careseekerDisplayId &&
      careproviderDisplayId &&
      loggedGroupDisplayId
    ) {
      createSealdSession?.({
        checkSealdGroupAccess: api.crypto.checkSealdGroupAccess,
        createEncryptionContext,
        createSealdAccess: api.crypto.createSealdAccess,
        entity: auctionRequest,
        entityType: "auction_request",
        entityUsersDisplayIds: [
          careseekerDisplayId,
          careproviderDisplayId,
        ].truthy(),
        loggedGroupDisplayId,
      })
        .then(() => {
          setIsProcessed(true);
        })
        .catch((err) => {
          console.error(
            "[useAuctionRequestSealdSession] - createSealdSession failed",
            err,
          );
        });
    }
  }, [
    careseekerDisplayId,
    careproviderDisplayId,
    hasEncryptionContext,
    loggedGroupDisplayId,
  ]);
  return isProcessed;
}

const SealdProcessedMark = ({
  auctionRequestId,
  isProcessed,
}: {
  auctionRequestId: number;
  isProcessed: boolean;
}) => (
  <input
    id={`request_${auctionRequestId}_seald_processed`}
    name="request_seald_processed"
    type="hidden"
    value={String(isProcessed)}
  />
);

type AuctionRequestEncryptionProviderProps = {
  auctionRequest: AuctionRequest;
  children: React.ReactNode;
  events: Array<Event>;
};

export function AuctionRequestEncryptionProvider({
  auctionRequest,
  children,
  events,
}: AuctionRequestEncryptionProviderProps) {
  const isSealdOnly = useIsLoggedSealdOnly();

  const account = useConnecterWithLoggedAccount();
  const privateKey = usePrivateKey();
  const [sessionKey, setSessionKey] = useState<SessionKey | undefined>(
    auctionRequest?.session_key,
  );
  const [updateSessionKeys] = useUpdateRequestSessionKeys({
    auctionRequestId: auctionRequest?.id,
    onCompleted: ({ sessionKeys }) => {
      const sessionKeyMap = sessionKeys.find(
        (key: SessionKey) => key.account_id === account?.id,
      );
      setSessionKey(sessionKeyMap);
    },
  });

  const isSealdOnlyRequest =
    isSealdOnly({
      newSealdSession: auctionRequest.seald_encryption_context,
      oldSession: auctionRequest.seald_encryption_context,
      // if the careseeker who created the auction request is seald only
      // or is the logged careprovider is seald only
    }) || !!auctionRequest.is_seald_only_encryption;

  const hasEncryption =
    (!isSealdOnlyRequest && Boolean(auctionRequest?.messenger_available)) ||
    Boolean(auctionRequest?.has_seald_encryption_context);
  let isProcessed = useAuctionRequestSealdSession(auctionRequest);
  isProcessed =
    (isSealdOnlyRequest || auctionRequest?.messenger_available) &&
    !auctionRequest?.has_seald_encryption_context
      ? isProcessed
      : true;

  if (!auctionRequest?.auction?.patient || !account) return null;
  if (!isProcessed) return <SpinnerPage id="seald_request_processed" />;

  const context = { auction_request_id: auctionRequest.id };
  const accounts = auctionRequest?.accounts?.filter(
    (acc) => isSealdOnlyRequest || !!acc?.public_key?.length,
  );

  // does this function the same as hasEncryption?
  const withEncryptionAccess = isSealdOnlyRequest
    ? !!auctionRequest.auction.patient.seald_encryption_context
    : !!getPatientSessionKey(account, auctionRequest.auction.patient);

  if (!hasEncryption)
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey: null,
          events,
          hasEncryption: false,
          isSealdOnly: isSealdOnlyRequest,
          userSupportsEncryption: false,
          withEncryptionAccess: false,
        }}
      >
        <SealdProcessedMark
          auctionRequestId={auctionRequest.id}
          isProcessed={isProcessed}
        />
        {children}
      </EncryptionContextProvider>
    );

  const [userSupportsEncryption, reason] = isEncryptionAvailableForUser(
    accounts,
    account,
    privateKey,
    auctionRequest,
    isSealdOnlyRequest,
  );

  if (!userSupportsEncryption)
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey: null,
          encryptionError: reason || undefined,
          events,
          hasEncryption: true,
          isSealdOnly: isSealdOnlyRequest,
          userSupportsEncryption:
            account?.account_type === ACCOUNT_TYPE_STAFF || false,
          withEncryptionAccess,
        }}
      >
        <SealdProcessedMark
          auctionRequestId={auctionRequest.id}
          isProcessed={isProcessed}
        />
        {children}
      </EncryptionContextProvider>
    );

  if (isSealdOnlyRequest) {
    return (
      <SealdEventsProvider
        account={account}
        events={events}
        sealdEncryptionContext={auctionRequest.seald_encryption_context}
        withEncryptionAccess={withEncryptionAccess}
      >
        <SealdProcessedMark
          auctionRequestId={auctionRequest.id}
          isProcessed={isProcessed}
        />
        {children}
      </SealdEventsProvider>
    );
  }

  return (
    <FetchKeysHOC
      account={account}
      accounts={accounts || null}
      context={context}
      events={events}
      privateKey={privateKey}
      resourceId={auctionRequest.id}
      sessionKey={sessionKey || null}
      updateSessionKey={updateSessionKeys}
      withEncryptionAccess={withEncryptionAccess}
    >
      <SealdProcessedMark
        auctionRequestId={auctionRequest.id}
        isProcessed={isProcessed}
      />
      {children}
    </FetchKeysHOC>
  );
}

function useAnnounceStatusUpdate(auctionRequest: AuctionRequest) {
  const { updateA11yMessage } = useA11yMessage();
  const statusTranslation = useStatusTranslation(auctionRequest);
  useUpdatedValue((_, oldStatus) => {
    // If the old status was undefined, it means that the request was still loading.
    // Only announce when we transition from one valid status to another valid status
    if (oldStatus && statusTranslation) {
      updateA11yMessage(statusTranslation);
    }
  }, auctionRequest.status);
}

export const AuctionRequestProvider = ({
  auctionRequest,
  children,
}: {
  auctionRequest: AuctionRequest;
  children: React.ReactNode;
}) => {
  const [, eventPayload, { querySignature: eventsQuery, refetch }] =
    useGetAuctionRequestEvents({
      id: auctionRequest.id,
      withEventLastSeens: true,
    });
  useOnRequestEventReceived(auctionRequest?.id ?? -1, {
    skip: !auctionRequest?.id,
  });

  useAnnounceStatusUpdate(auctionRequest);

  return (
    <AuctionRequestEncryptionProvider
      auctionRequest={auctionRequest}
      events={eventPayload?.events || []}
    >
      <AuctionRequestEventsContext.Provider
        value={{ eventsQuery, refetchEvents: refetch }}
      >
        {children}
      </AuctionRequestEventsContext.Provider>
    </AuctionRequestEncryptionProvider>
  );
};
